import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2, FileText, AlertTriangle, ClipboardCheck, Shield, Wrench,
  Clock, CheckCircle, XCircle, Calendar, TrendingUp, Eye, Lock,
  Users, MapPin, Briefcase, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

/**
 * ContractorPortalPage - Dashboard for contractor users
 * Shows only data within their service agreement scope
 */
const ContractorPortalPage = () => {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [capas, setCAPAs] = useState([]);
  const [userContext, setUserContext] = useState(null);

  const fetchData = useCallback(async () => {
    if (!api) return;
    
    try {
      setLoading(true);
      const [contextRes, agreementsRes, incidentsRes, capasRes] = await Promise.all([
        api.get('/multiorg/context/me'),
        api.get('/multiorg/contractor-agreements').catch(() => ({ data: [] })),
        api.get('/incidents?limit=10').catch(() => ({ data: [] })),
        api.get('/capas?limit=10').catch(() => ({ data: [] })),
      ]);
      
      setUserContext(contextRes.data);
      setAgreements(agreementsRes.data || []);
      setIncidents(incidentsRes.data || []);
      setCAPAs(capasRes.data || []);
    } catch (error) {
      console.error('Error fetching contractor data:', error);
      toast.error('Failed to load contractor portal data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check if user is a contractor
  const isContractor = userContext?.is_contractor;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  if (!isContractor) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Lock className="w-16 h-16 text-slate-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Contractor Portal</h2>
        <p className="text-slate-400">
          This portal is only accessible to contractor users with active service agreements.
        </p>
      </div>
    );
  }

  // Get contractor organization info
  const contractorMembership = userContext.memberships?.find(m => m.is_contractor);
  const contractorOrg = contractorMembership?.organization;

  // Count stats
  const activeAgreements = agreements.filter(a => a.status === 'active').length;
  const openIncidents = incidents.filter(i => i.status !== 'closed').length;
  const assignedCAPAs = capas.filter(c => c.assigned_to === user?.id).length;

  return (
    <div className="space-y-6" data-testid="contractor-portal-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="w-7 h-7 text-orange-400" />
            {t('contractor.title')}
          </h1>
          <p className="text-slate-400 mt-1">
            {contractorOrg?.name || t('contractor.contractors')}
          </p>
        </div>
        <Badge className="bg-orange-500/20 text-orange-400 px-3 py-1">
          Contractor Access
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Active Agreements</p>
                <p className="text-2xl font-bold">{activeAgreements}</p>
              </div>
              <FileText className="w-8 h-8 text-sky-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Open Incidents</p>
                <p className="text-2xl font-bold">{openIncidents}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Assigned CAPAs</p>
                <p className="text-2xl font-bold">{assignedCAPAs}</p>
              </div>
              <ClipboardCheck className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Clients Served</p>
                <p className="text-2xl font-bold">
                  {new Set(agreements.map(a => a.client_org_id)).size}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="agreements">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="agreements">Service Agreements</TabsTrigger>
          <TabsTrigger value="incidents">Related Incidents</TabsTrigger>
          <TabsTrigger value="capas">Assigned CAPAs</TabsTrigger>
        </TabsList>

        {/* Service Agreements Tab */}
        <TabsContent value="agreements" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle>Active Service Agreements</CardTitle>
              <CardDescription>
                Your organization's contracts with client airlines
              </CardDescription>
            </CardHeader>
            <CardContent>
              {agreements.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No service agreements found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {agreements.map(agreement => (
                    <div 
                      key={agreement.id}
                      className="p-4 bg-slate-800 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">
                              Agreement #{agreement.agreement_number}
                            </h3>
                            <Badge className={
                              agreement.status === 'active' 
                                ? 'bg-green-500/20 text-green-400'
                                : agreement.status === 'pending'
                                ? 'bg-amber-500/20 text-amber-400'
                                : 'bg-slate-500/20 text-slate-400'
                            }>
                              {agreement.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400 mt-1">
                            {agreement.service_type} • {agreement.service_description}
                          </p>
                        </div>
                        <div className="text-right text-sm text-slate-400">
                          <p>Valid until</p>
                          <p className="font-mono">
                            {new Date(agreement.effective_to).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex flex-wrap gap-2">
                        {agreement.allowed_modules?.map(module => (
                          <Badge key={module} variant="outline" className="text-xs">
                            {module}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="mt-3 flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {agreement.locations_covered?.length || 0} locations
                        </span>
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {agreement.departments_covered?.length || 0} departments
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {agreement.data_access_level || 'limited'} access
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle>Related Incidents</CardTitle>
              <CardDescription>
                Incidents within your service scope
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead>Report #</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incidents.map(incident => (
                    <TableRow key={incident.id} className="border-slate-800">
                      <TableCell className="font-mono">{incident.report_number}</TableCell>
                      <TableCell>{incident.title}</TableCell>
                      <TableCell>
                        {new Date(incident.occurrence_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          incident.severity === 'critical' ? 'bg-red-500' :
                          incident.severity === 'major' ? 'bg-orange-500' :
                          incident.severity === 'minor' ? 'bg-yellow-500' :
                          'bg-slate-500'
                        }>
                          {incident.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{incident.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {incidents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                        No incidents found within your service scope
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CAPAs Tab */}
        <TabsContent value="capas" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle>Assigned Corrective Actions</CardTitle>
              <CardDescription>
                CAPAs assigned to your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead>CAPA #</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {capas.map(capa => (
                    <TableRow key={capa.id} className="border-slate-800">
                      <TableCell className="font-mono">{capa.capa_number}</TableCell>
                      <TableCell>{capa.title}</TableCell>
                      <TableCell>
                        {new Date(capa.due_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          capa.status === 'closed' ? 'bg-green-500' :
                          capa.status === 'in_progress' ? 'bg-blue-500' :
                          capa.status === 'overdue' ? 'bg-red-500' :
                          'bg-amber-500'
                        }>
                          {capa.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          View <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {capas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                        No CAPAs assigned to your organization
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContractorPortalPage;
